import React from 'react';
import { useLanguage } from './LanguageContext';

const translations = {
  en: {
    preTitle: 'Meet Chaiffy',
    title1: 'What\'s in your fridge?',
    title2: 'We\'ll handle dinner.',
    subtitle: 'Take a photo. Get a recipe. It\'s that ridiculously simple. Chaiffy turns whatever you\'ve got into something worth eating.',
    cta: 'Cook Something Amazing',
    ctaSecondary: 'Watch How It Works',
    tagline: 'No grocery list. No recipe book. Just you, your phone, and whatever\'s in the kitchen.',
    floatCard1: 'Kabsa',
    floatCard1Sub: 'Ready in 45 min',
    floatCard2: 'Shawarma Bowl',
    floatCard2Sub: '5 ingredients found',
    floatCard3: 'Kunafa',
    floatCard3Sub: 'Dessert match!',
  },
  ar: {
    preTitle: 'تعرّف على Chaiffy',
    title1: 'ماذا في ثلاجتك؟',
    title2: 'نحن نتكفل بالعشاء.',
    subtitle: 'التقط صورة. احصل على وصفة. بهذه البساطة المذهلة. Chaiffy يحوّل أي شيء لديك إلى طبق يستحق التجربة.',
    cta: 'اطبخ شيئاً مذهلاً',
    ctaSecondary: 'شاهد كيف يعمل',
    tagline: 'بدون قائمة مشتريات. بدون كتاب وصفات. فقط أنت وهاتفك وما في المطبخ.',
    floatCard1: 'كبسة',
    floatCard1Sub: 'جاهزة في 45 دقيقة',
    floatCard2: 'شاورما بول',
    floatCard2Sub: 'تم العثور على 5 مكونات',
    floatCard3: 'كنافة',
    floatCard3Sub: 'وصفة حلى مطابقة!',
  }
};

const Hero = () => {
  const { language, isRTL } = useLanguage();
  const t = translations[language];

  return (
    <section className="relative overflow-hidden min-h-[90vh] flex items-center" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="absolute inset-0 bg-gradient-to-b from-emerald-50 via-white to-white"></div>
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-[10%] w-72 h-72 bg-emerald-200/40 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-[5%] w-96 h-96 bg-green-100/50 rounded-full blur-3xl"></div>
        <div className="absolute top-[40%] left-[50%] w-48 h-48 bg-amber-100/40 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto max-w-6xl px-4 relative z-10 py-16 md:py-24">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-5 py-2 bg-primary/10 text-primary rounded-full text-sm font-semibold mb-8 backdrop-blur-sm">
            <span className="w-1.5 h-1.5 bg-primary rounded-full animate-pulse"></span>
            {t.preTitle}
          </div>
          
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-dark mb-6 leading-[1.1] tracking-tight" style={{ fontFamily: 'Poppins, sans-serif' }}>
            {t.title1}<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-500 to-teal-600">{t.title2}</span>
          </h1>
          
          <p className="text-lg md:text-xl text-gray-500 mb-10 max-w-xl mx-auto leading-relaxed" style={{ fontFamily: 'Lato, sans-serif' }}>
            {t.subtitle}
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
            <a href="/start" className="w-full sm:w-auto btn-primary text-center flex items-center justify-center gap-2 text-base md:text-lg px-10 py-4">
              {t.cta}
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d={isRTL ? "M19 12H5M12 5l-7 7 7 7" : "M5 12h14M12 5l7 7-7 7"} />
              </svg>
            </a>
            <a href="#how-it-works" className="w-full sm:w-auto btn-secondary text-center text-base md:text-lg px-10 py-4">
              {t.ctaSecondary}
            </a>
          </div>

          <p className="text-sm text-gray-400 italic" style={{ fontFamily: 'Lato, sans-serif' }}>
            {t.tagline}
          </p>
        </div>

        <div className="grid grid-cols-3 gap-3 md:gap-6 max-w-2xl mx-auto">
          {[
            { emoji: '🍚', name: t.floatCard1, sub: t.floatCard1Sub, color: 'from-amber-50 to-orange-50', border: 'border-amber-200/60', delay: '0s' },
            { emoji: '🥙', name: t.floatCard2, sub: t.floatCard2Sub, color: 'from-emerald-50 to-green-50', border: 'border-emerald-200/60', delay: '0.1s' },
            { emoji: '🧁', name: t.floatCard3, sub: t.floatCard3Sub, color: 'from-pink-50 to-rose-50', border: 'border-pink-200/60', delay: '0.2s' },
          ].map((card, i) => (
            <div 
              key={i} 
              className={`bg-gradient-to-br ${card.color} border ${card.border} rounded-2xl p-4 md:p-5 text-center card-hover animate-fade-in-up`}
              style={{ animationDelay: card.delay }}
            >
              <div className="text-3xl md:text-4xl mb-2">{card.emoji}</div>
              <h4 className="font-bold text-dark text-sm md:text-base" style={{ fontFamily: 'Poppins, sans-serif' }}>{card.name}</h4>
              <p className="text-xs text-gray-400 mt-1">{card.sub}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;
